import { withFormik } from "formik";
import moment from "moment";
import * as Yup from "yup";

const namePattern = /^[A-Za-z0-9 ]+$/;
export const REGISTER_DOB_FORMAT = "MM/DD/YYYY";
export const DOB_FORMAT_VALIDATION_KEY = "DOB_FORMAT_VALIDATION";
const registerDobPattern =
  /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/;

export const registrationDobSchema = (
  requiredMessage = "SELECT_DOB_VALIDATION",
  formatMessage = DOB_FORMAT_VALIDATION_KEY
) =>
  Yup.string()
    .required(requiredMessage)
    .matches(registerDobPattern, formatMessage)
    .test("validDate", formatMessage, (value) => {
      if (!value) return true;
      return moment(value, REGISTER_DOB_FORMAT, true).isValid();
    })
    .test("maxDate", "DOB_FUTURE_VAL", (value) => {
      if (!value) return true;
      return moment(value, REGISTER_DOB_FORMAT, true).isSameOrBefore(
        moment(),
        "day"
      );
    })
    .test("minDate", "DOB_PAST_VAL", (value) => {
      if (!value) return true;
      return moment(value, REGISTER_DOB_FORMAT, true).isSameOrAfter(
        moment("01/01/1900", REGISTER_DOB_FORMAT),
        "day"
      );
    });

/** Min 15 chars; requires lower, upper, digit, and special character; no whitespace. */
export const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s\uD800-\uDFFF])[^\s\uD800-\uDFFF]{15,}$/;

export const formatRegisterDobInput = (raw) => {
  const digits = String(raw).replace(/\D/g, "").slice(0, 8);
  if (digits.length === 0) {
    return "";
  }
  if (digits.length <= 2) {
    return digits.length === 2 ? `${digits}/` : digits;
  }
  if (digits.length <= 4) {
    const monthDay = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits.length === 4 ? `${monthDay}/` : monthDay;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

export const formatRegisterDobForApi = (value) => {
  const display = normalizeRegisterDobDisplayValue(value);
  const parsed = moment(display, REGISTER_DOB_FORMAT, true);
  if (parsed.isValid()) {
    return parsed.format("YYYY-MM-DD");
  }

  const isoDate = moment(value, "YYYY-MM-DD", true);
  if (isoDate.isValid()) {
    return isoDate.format("YYYY-MM-DD");
  }

  return value;
};

export const normalizeRegisterDobDisplayValue = (value) => {
  if (!value) {
    return "";
  }

  const mmDdYyyy = moment(value, REGISTER_DOB_FORMAT, true);
  if (mmDdYyyy.isValid()) {
    return mmDdYyyy.format(REGISTER_DOB_FORMAT);
  }

  const isoDate = moment(value, "YYYY-MM-DD", true);
  if (isoDate.isValid()) {
    return isoDate.format(REGISTER_DOB_FORMAT);
  }

  return formatRegisterDobInput(value);
};

/** Fallback copy when i18n keys are not yet in the translation API */
export const registerValidationMessageFallbacks = {
  [DOB_FORMAT_VALIDATION_KEY]:
    "Date of birth must be in MM/DD/YYYY format.",
};

export const resolveRegisterValidationMessage = (key, translate) => {
  if (!key) {
    return "";
  }
  const translated = typeof translate === "function" ? translate(key) : key;
  if (translated && translated !== key) {
    return translated;
  }
  return registerValidationMessageFallbacks[key] || translated || key;
};
export const registerInitialValues = {
  firstName: "",
  lastName: "",
  preferredName: "",
  email: "",
  registrationCode: "",
  dob: "",
  zipCode: "",
  password: "",
  confirmPassword: "",
  categoryId: "",
  acceptedTerms: false,
};

export const registerValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim("SPACE_VALIDATION")
    .strict(true)
    .matches(namePattern, "SPECIAL_CHAR_VALIDATION")
    .min(1, "FIRST_NAME_REQ_VALIDATION")
    .required("FIRST_NAME_REQ_VALIDATION"),
  lastName: Yup.string()
    .trim("SPACE_VALIDATION")
    .strict(true)
    .matches(namePattern, "SPECIAL_CHAR_VALIDATION")
    .min(1, "LAST_NAME_REQ_VALIDATION")
    .required("LAST_NAME_REQ_VALIDATION"),
  preferredName: Yup.string()
    .trim("SPACE_VALIDATION")
    .strict(true)
    .test("preferredName", "SPECIAL_CHAR_VALIDATION", (value) => {
      if (!value || value.length === 0) return true;
      return namePattern.test(value);
    }),
  email: Yup.string()
    .trim()
    .email("VALID_EMAIL_VALIDATION")
    .required("EMAIL_REQ_VALIDATION"),
  registrationCode: Yup.string()
    .trim()
    .required("REGISTRATION_CODE_VALIDATION")
    .min(1, "REGISTRATION_CODE_VALIDATION"),
  dob: registrationDobSchema(),
  zipCode: Yup.string()
    .matches(/^[0-9]+$/, "ONLY_DIGITS_VALIDATION")
    .min(5, "ZIP_FIVE_CHAR_VALIDATION")
    .max(5, "ZIP_FIVE_CHAR_VALIDATION")
    .required("ZIP_CODE_VALIDATION"),
  password: Yup.string()
    .required("NUTRITION_REGISTER_VAL_PASSWORD_REQUIRED")
    .matches(PASSWORD_REGEX, "NUTRITION_REGISTER_VAL_PASSWORD_RULES"),
  confirmPassword: Yup.string()
    .required("NUTRITION_REGISTER_VAL_CONFIRM_PASSWORD")
    .oneOf([Yup.ref("password")], "NUTRITION_REGISTER_VAL_PASSWORD_MISMATCH"),
  categoryId: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      return value;
    })
    .typeError("CATEGORY_REQ_VALIDATION")
    .required("CATEGORY_REQ_VALIDATION"),
  acceptedTerms: Yup.boolean().oneOf([true], "ACK_VALIDATION"),
});

const formikEnhancer = withFormik({
  validationSchema: registerValidationSchema,
  mapPropsToValues: () => ({ ...registerInitialValues }),
  displayName: "RegisterForm",
  enableReinitialize: true,
  validateOnBlur: true,
  validateOnChange: true,
  isInitialValid: false,
});

export default formikEnhancer;
